import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { API, LOGO_URL } from "@/lib/scroll";
import { usePageMeta } from "@/hooks/usePageMeta";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  usePageMeta("Admin | Build Right Partners", "", true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("brp_token", data.token);
      navigate("/admin/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#060B18] border border-amber-500/20 px-4 py-3.5 text-sm text-amber-50 placeholder:text-slate-500 focus:outline-none focus:border-amber-400/60 focus:ring-1 focus:ring-amber-400/30 transition-colors";

  return (
    <main data-testid="admin-login-page" className="min-h-screen bg-[#060B18] flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md bg-[#0A1128]/80 backdrop-blur-xl border border-amber-500/20 p-10 shadow-[0_20px_50px_rgba(0,0,0,0.6)]"
      >
        <div className="flex flex-col items-center mb-8">
          <span className="h-14 w-14 rounded-sm overflow-hidden ring-1 ring-amber-400/40 mb-4">
            <img src={LOGO_URL} alt="Build Right Partners" className="h-full w-full object-cover" />
          </span>
          <h1 className="font-serif text-2xl font-semibold text-amber-50">Admin Portal</h1>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500/70">
            Build Right Partners
          </p>
        </div>

        <form onSubmit={submit} className="space-y-5" data-testid="admin-login-form">
          <input
            data-testid="admin-login-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Admin email"
            className={inputCls}
          />
          <input
            data-testid="admin-login-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className={inputCls}
          />
          {error && (
            <p data-testid="admin-login-error" className="text-sm text-red-400">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-submit-button"
            type="submit"
            disabled={loading}
            className="w-full rounded-full bg-gradient-to-r from-[#F3E5AB] via-[#D4AF37] to-[#AA7C11] py-3.5 font-mono text-xs uppercase tracking-[0.25em] font-semibold text-[#060B18] hover:shadow-[0_0_35px_rgba(212,175,55,0.35)] transition-shadow disabled:opacity-50"
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>

        <Link
          data-testid="admin-login-back-link"
          to="/"
          className="mt-6 block text-center font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 hover:text-amber-300 transition-colors"
        >
          ← Back to site
        </Link>
      </motion.div>
    </main>
  );
}
